import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../services/apiService';

// Async thunk for searching documents with caching
export const searchDocuments = createAsyncThunk(
  'search/searchDocuments',
  async ({ params, forceRefresh = false, fetchAll = false }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const cacheKey = `search_docs:${JSON.stringify(params)}`;

      // Check if we have cached data and not forcing refresh
      if (!forceRefresh && state.search.cache[cacheKey] && state.search.cache[cacheKey].expires > Date.now()) {
        return {
          data: state.search.cache[cacheKey].data,
          cached: true,
          cacheKey,
          timestamp: state.search.cache[cacheKey].timestamp
        };
      }

      if (forceRefresh) {
      }

      const now = Date.now();
      const timeSinceLastRequest = state.search.lastRequestTime ? (now - state.search.lastRequestTime) : Infinity;
      const hasAnyCache = Object.keys(state.search.cache).length > 0;

      if (timeSinceLastRequest < 200 && hasAnyCache) {
        return {
          data: { result: state.search.results || [] },
          cached: true,
          cacheKey: null,
          timestamp: new Date().toISOString(),
          blocked: true
        };
      }


      if (fetchAll) {
        const allResults = [];
        let page = 1;
        let totalCount = 0;

        while (true) {
          const response = await apiService.irb.searchDocuments({ ...params, pageNo: page, pageSize: params.pageSize || 100 });
          if (response.status !== 'success' || !response.data?.result) break;
          allResults.push(...response.data.result);
          const meta = response.data.metadata || {};
          totalCount = meta.totalCount || allResults.length;
          if (page >= (meta.totalPages || 1)) break;
          page++;
        }

        return {
          data: { result: allResults, metadata: { totalCount, totalPages: page } },
          cached: false,
          cacheKey,
          timestamp: new Date().toISOString(),
        };
      }

      const response = await apiService.irb.searchDocuments(params);
      if (response.status === 'success') {
        return {
          data: response.data,
          cached: false,
          cacheKey,
          timestamp: new Date().toISOString(),
        };
      }
      return rejectWithValue(response.message || 'Search failed');
    } catch (error) {
      console.error('[SEARCH_CACHE] ERROR:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Search failed');
    }
  }
);

const CACHE_TTL = 10 * 1000; // 10 seconds - reduced for real-time accuracy

const initialState = {
  results: [],
  loading: false,
  error: null,
  cache: {}, // Cache object with TTL
  lastSearchParams: null,
  total: 0,
  currentPage: 1,
  pageSize: 50,
  lastRequestTime: null, // Track last API request time to prevent spam
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    clearCache: (state) => {
      state.cache = {};
    },
    clearError: (state) => {
      state.error = null;
    },
    setSearchParams: (state, action) => {
      state.lastSearchParams = action.payload;
    },
    updateFromCache: (state, action) => {
      const { cacheKey } = action.payload;
      if (state.cache[cacheKey] && state.cache[cacheKey].expires > Date.now()) {
        state.results = state.cache[cacheKey].data.result || [];
        state.total = state.cache[cacheKey].data.metadata?.totalCount || state.cache[cacheKey].data.totalCount || 0;
        state.loading = false;
        state.error = null;
      }
    },
    // Cleanup expired cache entries
    cleanupCache: (state) => {
      const now = Date.now();
      Object.keys(state.cache).forEach(key => {
        if (state.cache[key].expires <= now) {
          delete state.cache[key];
        }
      });
    },
    addDocumentsFromSubmission: (state, action) => {
      const { acceptedDocuments } = action.payload;
      if (state.results && acceptedDocuments) {
        const newDocs = acceptedDocuments.filter(doc =>
          !state.results.some(r => r.uuid === doc.uuid)
        );
        state.results = [...newDocs, ...state.results];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchDocuments.pending, (state, action) => {
        // Only set loading if this is actually making an API call (not blocked or cached)
        const { params } = action.meta.arg;
        const cacheKey = `search_docs:${JSON.stringify(params)}`;
        const hasCache = state.cache[cacheKey] && state.cache[cacheKey].expires > Date.now();
        const timeSinceLastRequest = state.lastRequestTime ? (Date.now() - state.lastRequestTime) : Infinity;
        const hasAnyCache = Object.keys(state.cache).length > 0;

        // Allow request if no cache exists OR enough time has passed
        if (!hasCache && (!hasAnyCache || timeSinceLastRequest >= 200)) {
          state.loading = true;
          state.lastRequestTime = Date.now(); // Track request time
        }

        state.error = null;

        // If this is a cached request, update UI immediately
        if (hasCache) {
          state.results = state.cache[cacheKey].data.result || [];
          state.total = state.cache[cacheKey].data.metadata?.totalCount || state.cache[cacheKey].data.totalCount || 0;
        }
      })
      .addCase(searchDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const { data, cached, cacheKey, timestamp, blocked } = action.payload;

        // Don't update state if request was blocked
        if (blocked) {
          return;
        }

        if (data && data.result) {
          state.results = data.result;
          state.total = data.metadata?.totalCount || data.totalCount || 0;

          // Cache the results if not already cached
          if (!cached && cacheKey) {
            state.cache[cacheKey] = {
              data: data,
              timestamp: timestamp,
              expires: Date.now() + CACHE_TTL
            };
          }
        }
      })
      .addCase(searchDocuments.rejected, (state, action) => {
        state.loading = false;
        // action.payload is { message, response?: { status } } when rejected with full error object
        // or just a string when rejected with a simple message
        const payload = typeof action.payload === 'object' ? action.payload : { message: action.payload };
        const status = payload.response?.status;
        if (status === 429) {
          state.error = payload.message || 'Rate limit exceeded. Please wait before searching again.';
          // Keep results on rate limit (retry is appropriate)
        } else if (status >= 500) {
          state.error = payload.message || 'Server error. Please try again.';
          state.results = []; // Clear stale results on server errors
        } else {
          state.error = payload.message || 'Search failed.';
          state.results = []; // Clear for other errors too
        }
      });
  },
});

// Action to invalidate cache and force refresh
export const invalidateCacheAndRefresh = createAsyncThunk(
  'search/invalidateCacheAndRefresh',
  async (_, { getState, dispatch }) => {
    dispatch(clearCache());
    return true;
  }
);

export const { clearCache, clearError, setSearchParams, updateFromCache, cleanupCache, addDocumentsFromSubmission } = searchSlice.actions;
export default searchSlice.reducer;
